import axios from 'axios'
import { ResponseAPIWithPagination } from '../../../common/types/API.types'
import {
  CreateFleetPayload,
  DivergenceResponse,
  Fleet,
  PushResponse,
  PushResultEntry,
  UpdateFleetPayload,
} from '../types/fleet.types'
import { DimmingProfile } from '../../dimmingProfile'

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + '/api/v1'

// Dedicated error so the UI can surface the 409 CAS-conflict / 1-fleet-membership
// rejection distinctly from a generic backend error (plan §4 G2: concurrent
// writes and duplicate membership both return 409 Conflict).
export class FleetConflictError extends Error {
  constructor(message = 'Conflit de version ou appartenance multiple') {
    super(message)
    this.name = 'FleetConflictError'
  }
}

const wrapConflict = (err: any): never => {
  if (err?.response?.status === 409) {
    throw new FleetConflictError(
      err.response?.data?.error ?? 'Conflit de version ou appartenance multiple',
    )
  }
  throw err
}

// ── Fleets CRUD ─────────────────────────────────────────────────────────────
export const getAllFleets = async (
  page = 0,
  pageSize = 0,
): Promise<ResponseAPIWithPagination<Fleet>> => {
  const params = new URLSearchParams({
    currentPage: page.toString(),
    pageSize: pageSize.toString(),
  })
  return (
    await axios.get<ResponseAPIWithPagination<Fleet>>(
      `${BACKEND_URL}/fleets?${params.toString()}`,
    )
  ).data
}

export const getFleetById = async (fleetId: string): Promise<Fleet> => {
  return (await axios.get<Fleet>(`${BACKEND_URL}/fleets/${fleetId}`)).data
}

export const createFleet = async (
  payload: CreateFleetPayload,
): Promise<Fleet> => {
  try {
    return (await axios.post<Fleet>(`${BACKEND_URL}/fleets`, payload)).data
  } catch (err) {
    return wrapConflict(err)
  }
}

export const updateFleet = async (
  fleetId: string,
  payload: UpdateFleetPayload,
): Promise<Fleet> => {
  try {
    return (await axios.put<Fleet>(`${BACKEND_URL}/fleets/${fleetId}`, payload))
      .data
  } catch (err) {
    return wrapConflict(err)
  }
}

export const deleteFleet = async (fleetId: string): Promise<null> => {
  return (await axios.delete<null>(`${BACKEND_URL}/fleets/${fleetId}`)).data
}

// ── Dimming profiles CRUD ───────────────────────────────────────────────────
// Wire contract §9: the dimming-profile DOCUMENT is keyed by `fleet_id` (1:1
// with the fleet), addressed at /api/v1/dimming-profiles/{fleet_id}. The numeric
// `profile_id` is the 0x21 wire slot — server-allocated, read-only on the client
// and NEVER sent on create.
export const getProfileByFleetId = async (
  fleetId: string,
): Promise<DimmingProfile | null> => {
  try {
    return (
      await axios.get<DimmingProfile>(
        `${BACKEND_URL}/dimming-profiles/${fleetId}`,
      )
    ).data
  } catch (err: any) {
    if (err?.response?.status === 404) return null
    throw err
  }
}

export const createProfile = async (
  fleetId: string,
  profile: Omit<DimmingProfile, 'version' | 'profile_id'>,
): Promise<DimmingProfile> => {
  // §9: the server allocates profile_id atomically — never send a client-chosen
  // slot on create. Strip any stray profile_id/version before POSTing.
  const { profile_id, version, ...body } = profile as DimmingProfile
  void profile_id
  void version
  try {
    return (
      await axios.post<DimmingProfile>(
        `${BACKEND_URL}/dimming-profiles/${fleetId}`,
        body,
      )
    ).data
  } catch (err) {
    return wrapConflict(err)
  }
}

export const updateProfile = async (
  fleetId: string,
  profile: DimmingProfile,
): Promise<DimmingProfile> => {
  try {
    return (
      await axios.put<DimmingProfile>(
        `${BACKEND_URL}/dimming-profiles/${fleetId}`,
        profile,
      )
    ).data
  } catch (err) {
    return wrapConflict(err)
  }
}

// ── Version divergence + push/pull (plan §4 G3) ─────────────────────────────
export const getDivergence = async (
  fleetId: string,
): Promise<DivergenceResponse> => {
  return (
    await axios.get<DivergenceResponse>(
      `${BACKEND_URL}/fleets/${fleetId}/divergence`,
    )
  ).data
}

// Overwrite: push the cloud profile to every fleet member. Go wraps the
// per-device results under `results` (wire contract §7); unwrap so callers get
// the array directly and can surface partial failures (plan §4 G3, §9).
export const pushFleet = async (
  fleetId: string,
): Promise<PushResultEntry[]> => {
  // `?? []` : une flotte sans minigrid lié n'a aucune cible ; Go sérialise alors
  // la slice nil `results` en `null` (pas `[]`). Sans ce fallback, les
  // consommateurs (`results.filter(...)`) crashaient sur un cas nominal
  // (auto-push après ajout de membre / save de profil). Voir revue MR !2.
  return (
    (await axios.post<PushResponse>(`${BACKEND_URL}/fleets/${fleetId}/push`)).data
      .results ?? []
  )
}

// Download: pull a single momi's stored copy and overwrite the cloud profile.
export const pullFromMomi = async (
  fleetId: string,
  momiId: string,
): Promise<Fleet> => {
  return (
    await axios.post<Fleet>(`${BACKEND_URL}/fleets/${fleetId}/pull/${momiId}`)
  ).data
}

const fleetAPI = {
  getAllFleets,
  getFleetById,
  createFleet,
  updateFleet,
  deleteFleet,
  getProfileByFleetId,
  createProfile,
  updateProfile,
  getDivergence,
  pushFleet,
  pullFromMomi,
}

export default fleetAPI
